import React from 'react';
import { FinancialData } from '../types';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface FinancialChartsProps {
  companyData: FinancialData;
}

const FinancialCharts: React.FC<FinancialChartsProps> = ({ companyData }) => {

  return (
    <div className="financial-charts">
      <h2>財務分析: {companyData.company_name}</h2>
      <div className="company-info">
        <p><strong>証券コード:</strong> {companyData.security_code}</p>
        <p><strong>決算日:</strong> {companyData.filing_date}</p>
        <p><strong>企業タイプ:</strong> 
          <span style={{ color: companyData.company_type.color, fontWeight: 'bold' }}>
            {companyData.company_type.type}
          </span>
        </p>
        <p><strong>健全性スコア:</strong> {companyData.company_type.health_score}/5</p>
        <p><strong>自己資本比率:</strong> {companyData.equity_ratio.toFixed(2)}%</p>
      </div>

      <div className="financial-summary">
        <h3>主要財務指標</h3>
        <div className="metrics-grid">
          <div className="metric">
            <span className="label">売上高</span>
            <span className="value">{(companyData.revenue / 100000000).toFixed(1)}億円</span>
          </div>
          <div className="metric">
            <span className="label">営業利益</span>
            <span className="value">{(companyData.operating_profit / 100000000).toFixed(1)}億円</span>
          </div>
          <div className="metric">
            <span className="label">当期純利益</span>
            <span className="value">{(companyData.net_income / 100000000).toFixed(1)}億円</span>
          </div>
          <div className="metric">
            <span className="label">総資産</span>
            <span className="value">{(companyData.assets / 100000000).toFixed(1)}億円</span>
          </div>
        </div>
      </div>

      <div className="charts-container">
        {/* バランスシート図 - 統合表示 */}
        <div className="chart-section">
          <h3>バランスシート（貸借対照表）</h3>
          <p className="chart-description">資産 = 負債 + 純資産 のバランスを可視化</p>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart 
              data={[
                {
                  category: '資産',
                  assets: companyData.assets / 100000000
                },
                {
                  category: '負債・純資産',
                  liabilities: companyData.liabilities / 100000000,
                  net_assets: companyData.net_assets / 100000000
                }
              ]}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              barCategoryGap={0}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="category" hide />
              <YAxis />
              <Tooltip 
                formatter={(value: any, name: string) => {
                  const labels: { [key: string]: string } = {
                    assets: '資産合計',
                    liabilities: '負債',
                    net_assets: '純資産'
                  };
                  return [`${value.toFixed(1)}億円`, labels[name] || name];
                }}
                itemSorter={(item) => {
                  const order: { [key: string]: number } = { 'liabilities': 1, 'net_assets': 2, 'assets': 0 };
                  return order[item.dataKey as string] || 999;
                }}
              />
              <Legend formatter={(value: string) => {
                const labels: { [key: string]: string } = {
                  assets: '資産合計',
                  liabilities: '負債',
                  net_assets: '純資産'
                };
                return labels[value] || value;
              }} />
              <Bar dataKey="assets" fill="#8884d8" name="資産合計" />
              <Bar dataKey="net_assets" stackId="liability-equity" fill="#82ca9d" name="純資産" />
              <Bar dataKey="liabilities" stackId="liability-equity" fill="#ff7c7c" name="負債" />
            </BarChart>
          </ResponsiveContainer>
          
          {/* 自己資本比率を下部に表示 */}
          <div style={{ 
            textAlign: 'center', 
            marginTop: '20px', 
            padding: '15px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '8px',
            border: '2px solid #e9ecef'
          }}>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#495057' }}>
              自己資本比率: <span style={{ color: '#82ca9d', fontSize: '24px' }}>{companyData.equity_ratio.toFixed(1)}%</span>
              <span style={{ fontSize: '14px', color: '#666', marginLeft: '10px' }}>(目安: 30%以上)</span>
            </div>
            <div style={{ fontSize: '14px', color: '#6c757d', marginTop: '5px' }}>
              純資産 ÷ 資産合計 = {(companyData.net_assets / 100000000).toFixed(1)}億円 ÷ {(companyData.assets / 100000000).toFixed(1)}億円
            </div>
          </div>
        </div>

        {/* 損益計算書グラフ */}
        <div className="chart-section">
          <h3>損益計算書（階段構造）</h3>
          <p className="chart-description">売上高から各段階の利益までの階段を可視化</p>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={[{
                category: '損益階段',
                net_income: companyData.net_income / 100000000,
                operating_profit: companyData.operating_profit / 100000000,
                gross_profit: companyData.gross_profit / 100000000,
                revenue: companyData.revenue / 100000000
              }]}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              barCategoryGap={0}
            >
              <XAxis dataKey="category" />
              <YAxis />
              <Tooltip 
                formatter={(value: any, name: string) => {
                  const labels: { [key: string]: string } = {
                    net_income: '当期純利益',
                    operating_profit: '営業利益',
                    gross_profit: '売上総利益',
                    revenue: '売上高'
                  };
                  return [`${value.toFixed(1)}億円`, labels[name] || name];
                }}
                itemSorter={(item) => {
                  const order: { [key: string]: number } = { 
                    'revenue': 1, 
                    'gross_profit': 2, 
                    'operating_profit': 3, 
                    'net_income': 4 
                  };
                  return order[item.dataKey as string] || 999;
                }}
              />
              <Bar dataKey="net_income" fill="#ffb3ba" />
              <Bar dataKey="operating_profit" fill="#bae1ff" />
              <Bar dataKey="gross_profit" fill="#90ee90" />
              <Bar dataKey="revenue" fill="#ff9500" />
            </BarChart>
          </ResponsiveContainer>
          
          <div style={{ marginTop: '20px', fontSize: '14px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px', maxWidth: '600px', margin: '0 auto' }}>
              <div style={{ textAlign: 'center', padding: '10px', backgroundColor: '#fff5e6', borderRadius: '8px' }}>
                <div style={{ color: '#ff9500', fontWeight: 'bold', fontSize: '16px' }}>売上高</div>
                <div style={{ fontSize: '18px', marginTop: '5px' }}>{(companyData.revenue / 100000000).toFixed(1)}億円</div>
              </div>
              <div style={{ textAlign: 'center', padding: '10px', backgroundColor: '#f0fff0', borderRadius: '8px' }}>
                <div style={{ color: '#90ee90', fontWeight: 'bold', fontSize: '16px' }}>売上総利益</div>
                <div style={{ fontSize: '18px', marginTop: '5px' }}>{(companyData.gross_profit / 100000000).toFixed(1)}億円</div>
              </div>
              <div style={{ textAlign: 'center', padding: '10px', backgroundColor: '#e6f3ff', borderRadius: '8px' }}>
                <div style={{ color: '#bae1ff', fontWeight: 'bold', fontSize: '16px' }}>営業利益</div>
                <div style={{ fontSize: '18px', marginTop: '5px' }}>{(companyData.operating_profit / 100000000).toFixed(1)}億円</div>
              </div>
              <div style={{ textAlign: 'center', padding: '10px', backgroundColor: '#fff0f5', borderRadius: '8px' }}>
                <div style={{ color: '#ffb3ba', fontWeight: 'bold', fontSize: '16px' }}>当期純利益</div>
                <div style={{ fontSize: '18px', marginTop: '5px' }}>{(companyData.net_income / 100000000).toFixed(1)}億円</div>
              </div>
            </div>
          </div>
        </div>

        {/* キャッシュフロー分析図 */}
        <div className="chart-section">
          <h3>キャッシュフロー分析</h3>
          <p className="chart-description">営業・投資・財務キャッシュフローと企業の健全性タイプを可視化</p>
          
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '2rem', alignItems: 'start' }}>
            {/* キャッシュフロー棒グラフ */}
            <div>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={[{
                    category: 'キャッシュフロー',
                    operating_cf: companyData.operating_cf / 100000000,
                    investing_cf: companyData.investing_cf / 100000000,
                    financing_cf: companyData.financing_cf / 100000000
                  }]}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  barCategoryGap={0}
                >
                  <XAxis dataKey="category" />
                  <YAxis />
                  <Tooltip formatter={(value: any, name: string) => {
                    const labels: { [key: string]: string } = {
                      operating_cf: '営業CF',
                      investing_cf: '投資CF',
                      financing_cf: '財務CF'
                    };
                    const sign = (value as number) >= 0 ? '+' : '';
                    return [`${sign}${value.toFixed(1)}億円`, labels[name] || name];
                  }} />
                  <Legend formatter={(value: string) => {
                    const labels: { [key: string]: string } = {
                      operating_cf: '営業キャッシュフロー',
                      investing_cf: '投資キャッシュフロー', 
                      financing_cf: '財務キャッシュフロー'
                    };
                    return labels[value] || value;
                  }} />
                  <Bar dataKey="operating_cf" fill="#48bb78" name="営業CF" />
                  <Bar dataKey="investing_cf" fill="#ed8936" name="投資CF" />
                  <Bar dataKey="financing_cf" fill="#667eea" name="財務CF" />
                </BarChart>
              </ResponsiveContainer>

              {/* キャッシュフロー詳細情報 */}
              <div style={{ marginTop: '15px', fontSize: '14px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: '#48bb78', fontWeight: 'bold' }}>営業キャッシュフロー</div>
                    <div style={{ fontSize: '16px', marginTop: '5px' }}>
                      {companyData.operating_cf >= 0 ? '+' : ''}{(companyData.operating_cf / 100000000).toFixed(1)}億円
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '3px' }}>本業での現金創出力</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: '#ed8936', fontWeight: 'bold' }}>投資キャッシュフロー</div>
                    <div style={{ fontSize: '16px', marginTop: '5px' }}>
                      {companyData.investing_cf >= 0 ? '+' : ''}{(companyData.investing_cf / 100000000).toFixed(1)}億円
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '3px' }}>設備投資・M&A等</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: '#667eea', fontWeight: 'bold' }}>財務キャッシュフロー</div>
                    <div style={{ fontSize: '16px', marginTop: '5px' }}>
                      {companyData.financing_cf >= 0 ? '+' : ''}{(companyData.financing_cf / 100000000).toFixed(1)}億円
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '3px' }}>資金調達・返済等</div>
                  </div>
                </div>
              </div>
            </div>

            {/* 企業タイプ分析 */}
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '1.5rem', 
              borderRadius: '12px',
              border: '2px solid #e9ecef'
            }}>
              <h4 style={{ 
                marginBottom: '1rem', 
                color: '#495057',
                fontSize: '1.1rem',
                textAlign: 'center'
              }}>企業健全性分析</h4>
              
              <div style={{ 
                textAlign: 'center',
                marginBottom: '1.5rem'
              }}>
                <div style={{ 
                  backgroundColor: companyData.company_type.color,
                  color: 'white',
                  padding: '0.75rem 1rem',
                  borderRadius: '25px',
                  fontWeight: 'bold',
                  fontSize: '0.95rem',
                  marginBottom: '0.75rem'
                }}>
                  {companyData.company_type.type}
                </div>
                
                <div style={{ 
                  fontSize: '0.9rem',
                  color: '#6c757d',
                  marginBottom: '1rem'
                }}>
                  健全性スコア: <span style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                    {companyData.company_type.health_score}/5
                  </span>
                </div>
              </div>

              {/* キャッシュフローパターン解説 */}
              <div style={{ 
                backgroundColor: 'white',
                padding: '1rem',
                borderRadius: '8px',
                fontSize: '0.85rem',
                lineHeight: '1.4'
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: '#495057' }}>
                  キャッシュフローパターン
                </div>
                <div style={{ color: '#6c757d' }}>
                  {getCashFlowPattern(companyData.operating_cf, companyData.investing_cf, companyData.financing_cf)}
                </div>
              </div>

              {/* 健全性指標 */}
              <div style={{ 
                marginTop: '1rem',
                padding: '0.75rem',
                backgroundColor: 'white',
                borderRadius: '8px',
                fontSize: '0.85rem'
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: '#495057' }}>
                  主要指標
                </div>
                <div style={{ display: 'grid', gap: '0.25rem', color: '#6c757d' }}>
                  <div>自己資本比率: <strong>{companyData.equity_ratio.toFixed(1)}%</strong></div>
                  <div>営業利益率: <strong>{((companyData.operating_profit / companyData.revenue) * 100).toFixed(1)}%</strong></div>
                  <div>ROA: <strong>{((companyData.net_income / companyData.assets) * 100).toFixed(1)}%</strong></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// キャッシュフローパターンの解説を取得する関数
const getCashFlowPattern = (operating: number, investing: number, financing: number) => {
  if (operating > 0 && investing < 0 && financing < 0) {
    return "理想的パターン：営業活動で得た資金で投資を行い、借金返済も実施している健全な企業";
  } else if (operating > 0 && investing < 0 && financing > 0) {
    return "成長投資パターン：営業利益を投資に回し、追加資金も調達して積極的に成長を目指している";
  } else if (operating > 0 && investing > 0 && financing < 0) {
    return "資産売却パターン：本業好調で資産売却も行い、借金返済を進めている";
  } else if (operating < 0 && investing < 0 && financing > 0) {
    return "要注意パターン：本業不調で投資も行っており、資金調達で補填している状況";
  } else if (operating < 0 && investing > 0 && financing > 0) {
    return "危険パターン：本業不調のため資産売却と資金調達で資金を確保している";
  } else {
    return "その他のパターン：個別の状況分析が必要";
  }
};

export default FinancialCharts;